import { observable, computed, action, toJS } from 'mobx'
import { v4 as uuid } from 'uuid'

import PersistenceStore from 'stores/persistence/PersistenceStore'
import { ITableName } from 'stores/persistence/types'
import { IBaseListItem } from './BaseListItem'

export interface IBaseListOptions {
  idbTableName?: ITableName
}
export interface IBaseListAddOptions {
  skipPersist?: boolean
}

export default class BaseListStore<
  IMember extends IBaseListItem,
  IMemberCreatePayload
> {
  @observable private type: any
  @observable private readonly idbTableName: ITableName | null = null
  @observable private members: { [id: string]: IMember } = {}

  constructor(type: any, { idbTableName }: IBaseListOptions = {}) {
    this.type = type
    this.idbTableName = idbTableName ?? null

    // this.restore will be called by BootstrapStore
  }

  @computed
  get values(): { [id: string]: IMember } {
    return this.members
  }

  @computed
  get size(): number {
    return Object.keys(this.members).length
  }

  /**
   * Restore/Load/Pull existing members from IndexedDB
   */
  @action
  restore = async () => {
    if (this.idbTableName) {
      const members = await PersistenceStore.load(this.idbTableName)
      members.forEach((m: any) => {
        if (!m) {
          console.warn(
            `Attempt to restore a null member from IndexedDB, ignoring`,
            { member: m, idbTableName: this.idbTableName }
          )
          return
        }

        this.add(m, { skipPersist: true })
      }) // `any` here is okay because types are checked BEFORE saving to DB
    }

    return Promise.resolve()
  }

  /**
   * Returns a member using its ID if found, null otherwise
   *
   * @param id ID of the member requested
   */
  get(id: string | null | undefined): IMember | null {
    if (!id) {
      return null
    }

    return this.members[id] || null
  }

  /**
   * Creates a new member in-memory. Also saves to IndexedDB if `idbTableName` option is set
   *
   * @param member The member to add
   */
  @action
  add(
    member: Omit<IMemberCreatePayload, 'id'> & { id?: string }, // Making ID optional
    { skipPersist = false }: IBaseListAddOptions = {}
  ): IMember {
    if (!member.id) {
      // If an ID is not provided, create one
      member.id = uuid()
    }

    let createdMember: IMember
    if (this.members[member.id]) {
      // First, try to find this member, if it exists, just update it
      createdMember = this.members[member.id]
      createdMember.update(member, { skipPersist })
    } else {
      // If such a member is not found, create one
      const MemberClass = this.type
      createdMember = new MemberClass(member)
      createdMember.idbTableName = this.idbTableName // Tell the child which table to persist to as well. The outside world does not need to know about this

      this.members[member.id] = createdMember

      // If the `idbTableName` option is set, also persist to IndexedDB
      if (!skipPersist && this.idbTableName) {
        PersistenceStore.save(this.idbTableName, createdMember.serialize())
      }
    }

    return createdMember
  }

  /**
   * Removes an in-memory member. Also removes to IndexedDB if `idbTableName` option is set
   *
   * @param memberId ID of the member to remove
   */
  @action
  remove(memberId: string): IMember {
    const removedMember = this.members[memberId]
    if (removedMember) {
      delete this.members[memberId]
    }

    if (this.idbTableName) {
      PersistenceStore.remove(this.idbTableName, memberId)
    }

    return removedMember
  }

  /**
   * Removes all members from memory & IndexedDB (if `idbTableName` option is set)
   */
  @action
  clear(): void {
    this.members = {}

    if (this.idbTableName) {
      PersistenceStore.clear(this.idbTableName)
    }
  }

  @action
  toJS() {
    return toJS(this)
  }
}
